import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, ShieldCheckIcon, FileTextIcon } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
const privacyContent = {
  title: 'Política de Privacidad',
  sections: [{
    heading: 'Información que Recopilamos',
    body: 'Recopilamos únicamente la información necesaria para procesar tu compra: nombre, dirección de correo electrónico y datos de pago. No compartimos, vendemos ni alquilamos tu información personal a terceros.'
  }, {
    heading: 'Uso de la Información',
    body: 'Tu información se utiliza exclusivamente para: enviar el producto digital adquirido a tu correo electrónico, comunicarnos contigo sobre tu pedido y mejorar nuestros productos y servicios.'
  }, {
    heading: 'Protección de Datos',
    body: 'Utilizamos medidas de seguridad estándar de la industria para proteger tu información personal. Todos los pagos se procesan a través de plataformas seguras con encriptación SSL.'
  }, {
    heading: 'Cookies',
    body: 'Nuestro sitio puede utilizar cookies para mejorar tu experiencia de navegación. Puedes configurar tu navegador para rechazar cookies, aunque esto podría afectar algunas funcionalidades del sitio.'
  }, {
    heading: 'Tus Derechos',
    body: 'Tienes derecho a solicitar acceso, corrección o eliminación de tus datos personales en cualquier momento. Para ejercer estos derechos, contáctanos a través del correo proporcionado en tu confirmación de compra.'
  }]
};
const termsContent = {
  title: 'Términos y Condiciones',
  sections: [{
    heading: 'Producto Digital',
    body: 'El Recetario Rejuvenece es un producto digital en formato PDF. Una vez completada la compra, recibirás acceso inmediato al material a través del correo electrónico proporcionado.'
  }, {
    heading: 'Uso Personal',
    body: 'El contenido adquirido es para uso personal y no comercial. Queda prohibida la reproducción, distribución o reventa del material sin autorización expresa por escrito.'
  }, {
    heading: 'Descargo de Responsabilidad',
    body: 'Las recetas e información contenidas en este recetario son de carácter informativo y educativo. No sustituyen el consejo médico profesional. Consulta con tu médico antes de realizar cambios significativos en tu alimentación, especialmente si tienes condiciones de salud preexistentes.'
  }, {
    heading: 'Política de Reembolso',
    body: 'Debido a la naturaleza digital del producto, todas las ventas son finales. Si experimentas problemas técnicos para acceder al material, contáctanos y te ayudaremos a resolver cualquier inconveniente.'
  }, {
    heading: 'Modificaciones',
    body: 'Nos reservamos el derecho de modificar estos términos en cualquier momento. Los cambios serán efectivos inmediatamente después de su publicación en esta página.'
  }]
};
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const openModal = (type: ModalType) => {
    setActiveModal(type);
    document.body.style.overflow = 'hidden';
  };
  const closeModal = () => {
    setActiveModal(null);
    document.body.style.overflow = '';
  };
  const content = activeModal === 'privacy' ? privacyContent : termsContent;
  const ModalIcon = activeModal === 'privacy' ? ShieldCheckIcon : FileTextIcon;
  return <>
      <footer className="bg-espresso text-cream/60 py-8 px-4" data-id="element-36">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4" data-id="element-37">
          <p className="text-sm" data-id="element-38">
            © {new Date().getFullYear()} Recetario Rejuvenece. Todos los
            derechos reservados.
          </p>
          <div className="flex items-center gap-6" data-id="element-39">
            <button onClick={() => openModal('privacy')} className="text-sm text-cream/50 hover:text-cream transition-colors duration-200 underline underline-offset-2" data-id="element-40">
              Política de Privacidad
            </button>
            <span className="text-cream/20" data-id="element-41">|</span>
            <button onClick={() => openModal('terms')} className="text-sm text-cream/50 hover:text-cream transition-colors duration-200 underline underline-offset-2" data-id="element-42">
              Términos y Condiciones
            </button>
          </div>
        </div>
      </footer>

      <AnimatePresence data-id="element-43">
        {activeModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-espresso/60 backdrop-blur-sm" onClick={closeModal} data-id="element-44">
            <motion.div initial={{
          opacity: 0,
          y: 30,
          scale: 0.95
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: 20,
          scale: 0.95
        }} transition={{
          duration: 0.3,
          ease: 'easeOut'
        }} className="bg-cream rounded-3xl shadow-warm-lg max-w-2xl w-full max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-45">
              {/* Header */}
              <div className="flex items-center justify-between px-8 py-6 border-b border-espresso/10" data-id="element-46">
                <div className="flex items-center gap-3" data-id="element-47">
                  <ModalIcon className="w-6 h-6 text-sage" data-id="element-48" />
                  <h2 className="text-2xl font-bold text-espresso font-serif" data-id="element-49">
                    {content.title}
                  </h2>
                </div>
                <button onClick={closeModal} className="p-2 rounded-full hover:bg-espresso/10 transition-colors duration-200" aria-label="Cerrar" data-id="element-50">
                  <XIcon className="w-5 h-5 text-espresso/60" data-id="element-51" />
                </button>
              </div>

              {/* Content */}
              <div className="px-8 py-6 overflow-y-auto max-h-[calc(80vh-88px)] space-y-6" data-id="element-52">
                {content.sections.map((section, index) => <div key={index} data-id="element-53">
                    <h3 className="text-lg font-bold text-espresso mb-2" data-id="element-54">
                      {section.heading}
                    </h3>
                    <p className="text-espresso/80 leading-relaxed" data-id="element-55">
                      {section.body}
                    </p>
                  </div>)}
                <p className="text-sm text-espresso/50 pt-4 border-t border-espresso/10" data-id="element-56">
                  Última actualización:{' '}
                  {new Date().toLocaleDateString('es-ES', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
                </p>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}